import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Modal, Box, Typography, TextField, 
  MenuItem, InputLabel, Select, FormControl, Grid, CircularProgress
} from "@mui/material";
import { db, auth } from '../utils/firebase'; 
import { doc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { GENDER_OPTIONS, VETERAN_OPTIONS, DISABILITY_OPTIONS, COLLECTIONS, modalStyle, inputStyle } from '../utils/constants';

const INITIAL_FORM_DATA = {
  fullName: '', role: '', email: '', phone: '', gender: '', veteranStatus: '', disabled: 'No'
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * AddEmployeeModal - Enterprise-grade modal component for adding and editing employee data
 * Features comprehensive validation, error handling, and accessibility compliance
 * @param {Object} props - Component props
 * @param {boolean} props.open - Modal open state
 * @param {Function} props.onClose - Modal close handler
 * @param {Object} props.currentClient - Current client context
 * @param {Object} props.employeeData - Employee data for editing
 * @param {Function} props.onUpdateSuccess - Success callback
 */
function AddEmployeeModal({ open, onClose, currentClient, employeeData, onUpdateSuccess }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [error, setError] = useState('');

  const isEditMode = !!employeeData;

  const isFormIncomplete = 
    !formData.fullName || 
    !formData.role || 
    !formData.email || 
    !formData.phone || 
    !formData.gender || 
    !formData.veteranStatus || 
    !formData.disabled;

  useEffect(() => {
    if (open) {
      if (isEditMode && employeeData) {
        setFormData({ ...INITIAL_FORM_DATA, ...employeeData });
      } else {
        setFormData(INITIAL_FORM_DATA);
      }
      setError('');
    }
  }, [employeeData, open, isEditMode]);

  /**
   * Handles form input changes
   * @param {Event} e - Change event
   */
  const handleChange = useCallback((e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  }, [formData]);

  const handleClose = () => {
    setError('');
    onClose();
  };

  /**
   * Handles form submission with validation and Firebase operations
   * Performs comprehensive validation, database operations, and error handling
   */
  const handleSubmit = async () => {
    if (isFormIncomplete) {
      setError('Please fill in all required fields.');
      return;
    }

    if (!EMAIL_REGEX.test(formData.email)) {
      setError('Please enter a valid email address.');
      return;
    }

    if (!/^[0-9\-\s()+]{7,20}$/.test(formData.phone)) {
      setError('Please enter a valid phone number.');
      return;
    }

    const user = auth.currentUser;
    const targetId = currentClient?.client_id || currentClient?.id;

    if (!user || !targetId) {
      setError('Error: Missing user or client information.');
      return;
    }

    setLoading(true);

    try {
      const clientRef = doc(db, COLLECTIONS.USERS, user.uid, COLLECTIONS.CLIENT_ORG, targetId);
        console.log("", db, user.uid, targetId);

      if (isEditMode) {
        const docSnap = await getDoc(clientRef);
        if (docSnap.exists()) {
          const currentEmployees = docSnap.data().employees || [];
          const updatedEmployees = currentEmployees.map(emp => 
            emp.id === employeeData.id ? { ...formData, updatedAt: new Date().toISOString() } : emp
          );
          await updateDoc(clientRef, { employees: updatedEmployees });
        }
      } else {
        await updateDoc(clientRef, {
          employees: arrayUnion({
            ...formData,
            id: Date.now().toString(),
            createdAt: new Date().toISOString()
          })
        });
      }

      if (onUpdateSuccess) onUpdateSuccess();
      onClose();
    } catch (error) {
      console.error("Firebase Error:", error);
      alert("Operation failed: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="add-employee-title" aria-describedby="add-employee-description">
      <Box sx={modalStyle}>
        <Typography id="add-employee-title" variant="h6" color="white" align="center" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
          {isEditMode ? `Edit Details for ${employeeData?.fullName}` : "Add New Employee"}
        </Typography>

        {error && (
          <Typography variant="body2" color="error" align="center" sx={{ mb: 1 }}>
            {error}
          </Typography>
        )}
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField required fullWidth label="Full Name" name="fullName" value={formData.fullName} onChange={handleChange} sx={inputStyle} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField required fullWidth label="Job Role" name="role" value={formData.role} onChange={handleChange} sx={inputStyle} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField required type="email" fullWidth label="Email" name="email" value={formData.email} onChange={handleChange} sx={inputStyle} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField required type="tel" fullWidth label="Phone" name="phone" value={formData.phone} onChange={handleChange} sx={inputStyle} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl required fullWidth sx={inputStyle}>
              <InputLabel sx={{ color: 'white' }}>Gender</InputLabel>
              <Select name="gender" label="Gender" value={formData.gender} onChange={handleChange}>
                {GENDER_OPTIONS.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl required fullWidth sx={inputStyle}>
              <InputLabel sx={{ color: 'white' }}>Veteran Status</InputLabel>
              <Select name="veteranStatus" label="Veteran Status" value={formData.veteranStatus} onChange={handleChange}>
                {VETERAN_OPTIONS.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl required fullWidth sx={inputStyle}>
              <InputLabel sx={{ color: 'white' }}>Disability Status</InputLabel>
              <Select name="disabled" label="Disability Status" value={formData.disabled} onChange={handleChange}>
                {DISABILITY_OPTIONS.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 4 }}>
          <button className="dashbord-button3 button3" onClick={handleSubmit} disabled={loading} style={{ width: '150px', opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}>
            {loading ? <CircularProgress size={20} color="inherit" /> : (isEditMode ? "SAVE" : "ADD")}
          </button>
          <button className="dashbord-button3 button3" onClick={handleClose} style={{ width: '150px' }}>
            Cancel
          </button>
        </Box>
      </Box>
    </Modal>
  );
}

AddEmployeeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentClient: PropTypes.object,
  employeeData: PropTypes.object,
  onUpdateSuccess: PropTypes.func
};

AddEmployeeModal.defaultProps = {
  currentClient: null,
  employeeData: null,
  onUpdateSuccess: null
};

export default AddEmployeeModal;